<template>
  <LayoutDashboard>
    <div class="d-flex align-items-center">
      <div>
        <h5 class="font-weight-bold m-0">
          Dashboard
        </h5>
        <small class="text-info font-weight-bold">
          {{ userRoleName }}
        </small>
      </div>
      <button
        class="btn btn-sm btn-outline-info ml-auto"
        type="button"
        @click="openFilterModal"
      >
        <i class="fa fa-filter mr-2" />
        Filter
      </button>
    </div>
    <div class="row mt-3">
      <div class="col-12 col-lg-9">
        <KpiCards
          :stats="stats"
          @openUnsubmittedResponse="openUnsubmittedResponseModal"
        />
      </div>
      <div class="col-12 col-lg-3 mt-3 mt-lg-0">
        <ContractorDonut
          class="h-100"
          :contractor-id.sync="contractorId"
          :label.sync="contractor.label"
          :value="contractor.value"
          :total="contractor.total"
        />
      </div>
    </div>

    <div class="row mt-3 align-items-stretch">
      <div class="col-12 col-lg-9">
        <div class="row align-items-stretch">
          <div class="col-12 col-lg-4">
            <FdmResult
              :result="fdmResult"
              :total-responded="stats.totalResponded"
            />
          </div>
          <div class="col-12 col-lg-8 mt-3 mt-lg-0">
            <FdmPerformance
              :categories="fdmPerformance.categories"
              :series="fdmPerformance.series"
            />
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-3 mt-3 mt-lg-0">
        <SummaryInsight
          class="h-100"
          :items="summaryInsight"
          :period="filterForm.period"
        />
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-12 col-lg-4">
        <DepartmentChart :items="department.items" />
      </div>
      <div class="col-12 col-lg-4 mt-3 mt-lg-0">
        <RiskBars :items="risk.items" />
      </div>
      <div class="col-12 col-lg-4 mt-3 mt-lg-0">
        <TotalSubmitterGauge
          :fit="stats.fit"
          :total-responded="stats.totalResponded"
        />
      </div>
    </div>

    <div
      v-if="unsubmittedModalOpen"
      class="fdm-modal-backdrop"
      @click.self="closeUnsubmittedResponseModal"
    >
      <div class="fdm-modal fdm-modal-wide">
        <div class="fdm-modal-header">
          <div class="font-weight-bold">
            Unsubmitted Response
          </div>
          <button
            class="fdm-modal-close"
            type="button"
            @click="closeUnsubmittedResponseModal"
          >
            <i class="fa fa-times" />
          </button>
        </div>

        <div class="fdm-modal-body">
          <div class="fdm-unsubmitted-card">
            <div class="d-flex flex-wrap align-items-center">
              <div class="d-flex align-items-center mr-3">
                <small class="text-muted mr-2">Show</small>
                <select
                  v-model.number="unsubmittedPerPage"
                  class="form-control form-control-sm"
                  style="width: 84px;"
                >
                  <option :value="10">
                    10
                  </option>
                  <option :value="25">
                    25
                  </option>
                  <option :value="50">
                    50
                  </option>
                </select>
                <small class="text-muted ml-2">entries</small>
              </div>

              <div class="ml-auto d-flex align-items-center">
                <button
                  class="btn btn-outline-info btn-sm font-weight-bold mr-2"
                  type="button"
                  @click="openUnsubmittedFilterModal"
                >
                  <i class="fa fa-filter mr-2" />
                  Filter
                </button>
                <div
                  class="input-group input-group-sm"
                  style="width: 220px;"
                >
                  <div class="input-group-prepend">
                    <span class="input-group-text bg-white">
                      <i class="fa fa-search text-muted" />
                    </span>
                  </div>
                  <input
                    v-model="unsubmittedSearch"
                    class="form-control"
                    type="text"
                    placeholder="Search"
                  >
                </div>
              </div>
            </div>

            <div class="table-responsive mt-3">
              <table class="table table-sm table-borderless fdm-table">
                <thead>
                  <tr class="text-muted">
                    <th>Full Name</th>
                    <th>Company Name</th>
                    <th>Department</th>
                    <th>Position</th>
                    <th class="text-right">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in unsubmittedPageRows"
                    :key="row.id"
                  >
                    <td>{{ row.fullName }}</td>
                    <td>{{ row.companyName }}</td>
                    <td>{{ row.department }}</td>
                    <td>{{ row.position }}</td>
                    <td class="text-right">
                      <span
                        class="status-pill"
                        :class="row.status === 'Contractor' ? 'status-contractor' : 'status-employee'"
                      >
                        {{ row.status }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!unsubmittedPageRows.length">
                    <td
                      colspan="5"
                      class="text-center text-muted py-4"
                    >
                      No data
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="fdm-unsubmitted-footer">
              <small class="text-muted">
                Showing {{ unsubmittedFrom }} to {{ unsubmittedTo }} of {{ unsubmittedTotal }} entries
              </small>
              <nav aria-label="pagination">
                <ul class="pagination pagination-sm mb-0 fdm-pagination">
                  <li
                    class="page-item fdm-page-nav fdm-page-prev"
                    :class="{ disabled: unsubmittedPage === 1 }"
                  >
                    <button
                      class="page-link"
                      type="button"
                      @click="setUnsubmittedPage(unsubmittedPage - 1)"
                    >
                      <i class="fa fa-chevron-left" />
                    </button>
                  </li>
                  <li
                    v-for="p in unsubmittedPages"
                    :key="p"
                    class="page-item fdm-page-number"
                    :class="{ active: p === unsubmittedPage }"
                  >
                    <button
                      class="page-link"
                      type="button"
                      @click="setUnsubmittedPage(p)"
                    >
                      {{ p }}
                    </button>
                  </li>
                  <li
                    class="page-item fdm-page-nav fdm-page-next"
                    :class="{ disabled: unsubmittedPage === unsubmittedPageCount }"
                  >
                    <button
                      class="page-link"
                      type="button"
                      @click="setUnsubmittedPage(unsubmittedPage + 1)"
                    >
                      <i class="fa fa-chevron-right" />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="unsubmittedFilterModalOpen"
      class="fdm-modal-backdrop fdm-modal-backdrop-top"
      @click.self="closeUnsubmittedFilterModal"
    >
      <div class="fdm-modal">
        <div class="fdm-modal-header">
          <div class="font-weight-bold">
            Filter
          </div>
          <button
            class="fdm-modal-close"
            type="button"
            @click="closeUnsubmittedFilterModal"
          >
            <i class="fa fa-times" />
          </button>
        </div>

        <div class="fdm-modal-body">
          <div class="row">
            <div class="col-12 col-sm-6">
              <div class="mb-1">
                <small class="font-weight-bold">Company Name</small>
              </div>
              <v-select
                ref="unsubmittedCompanySelect"
                v-model="unsubmittedFilterForm.companyName"
                class="sm"
                label="name"
                :options="unsubmittedCompanyOptions"
                :reduce="getReduceName"
                :selectable="option => !option.hasOwnProperty('group')"
                :clearable="true"
                placeholder="Select Company Name"
                @open="onCompanyOpen"
              >
                <template #option="{ group, name }">
                  <div v-if="group">
                    {{ group }}
                  </div>
                  {{ name }}
                </template>
              </v-select>
            </div>
            <div class="col-12 col-sm-6 mt-3 mt-sm-0">
              <div class="mb-1">
                <small class="font-weight-bold">Department</small>
              </div>
              <v-select
                v-model="unsubmittedFilterForm.department"
                class="sm"
                :options="unsubmittedDepartmentOptions"
                :clearable="true"
                placeholder="Select Department"
              />
            </div>
          </div>

          <div class="row mt-3">
            <!-- <div class="col-12 col-sm-6">
              <div class="mb-1">
                <small class="font-weight-bold">Position</small>
              </div>
              <v-select
                v-model="unsubmittedFilterForm.position"
                class="sm"
                :options="unsubmittedPositionOptions"
                :clearable="true"
                placeholder="Select Position"
              />
            </div> -->
            <div class="col-12 col-sm-6 mt-3 mt-sm-0">
              <div class="mb-1">
                <small class="font-weight-bold">Status</small>
              </div>
              <v-select
                v-model="unsubmittedFilterForm.status"
                class="sm"
                :options="unsubmittedStatusOptions"
                :clearable="true"
                placeholder="Select Employee"
              />
            </div>
          </div>
        </div>

        <div class="fdm-modal-footer">
          <button
            class="btn btn-outline-info btn-sm font-weight-bold"
            type="button"
            @click="resetUnsubmittedFilter"
          >
            Reset
          </button>
          <button
            class="btn btn-info btn-sm font-weight-bold ml-2"
            type="button"
            @click="applyUnsubmittedFilter"
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="filterModalOpen"
      class="fdm-modal-backdrop"
      @click.self="closeFilterModal"
    >
      <div class="fdm-modal">
        <div class="fdm-modal-header">
          <div class="font-weight-bold">
            Filter
          </div>
          <button
            class="fdm-modal-close"
            type="button"
            @click="closeFilterModal"
          >
            <i class="fa fa-times" />
          </button>
        </div>

        <div class="fdm-modal-body">
          <div class="row">
            <div class="col-12 col-sm-6">
              <div class="mb-1">
                <small class="font-weight-bold">Period</small>
              </div>
              <v-select
                v-model="filterForm.period"
                class="sm"
                :clearable="false"
                :options="periodOptions"
              />
            </div>
            <div class="col-12 col-sm-6 mt-3 mt-sm-0">
              <div class="mb-1">
                <small class="font-weight-bold">Date</small>
              </div>
              <date-picker
                v-model="filterForm.dateRange"
                class="col-12 p-0"
                range
                :type="filterForm.period === 'Daily' ? 'date' : 'month'"
                placeholder="Select date range"
              />
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12 col-sm-6">
              <div class="mb-1">
                <small class="font-weight-bold">Department</small>
              </div>
              <v-select
                ref="workUnitSelect"
                v-model="filterForm.department"
                class="sm"
                :clearable="!isFunctionHeadSite"
                :options="workUnitOptions"
                :disabled="workUnits.loading || isFunctionHeadSite"
                placeholder="Select Department"
                @open="onWorkUnitOpen"
                @search="onWorkUnitSearch"
              />
            </div>
            <div class="col-12 col-sm-6 mt-3 mt-sm-0">
              <div class="mb-1">
                <!-- dev -->
                <small class="font-weight-bold">Work Location</small>
              </div>
              <v-select
                v-model="filterForm.workLocation"
                class="sm"
                :clearable="!(isAdministratorSite || isFunctionHeadSite)"
                :close-on-select="true"
                :disabled="isAdministratorSite || isFunctionHeadSite"
                label="name"
                :options="workLocationsCompany"
                :reduce="getReduceName"
                :selectable="option => !option.hasOwnProperty('group')"
                placeholder="Select Work Location"
                multiple
              >
                <template #option="{ group, name }">
                  <div v-if="group">
                    {{ group }}
                  </div>
                  {{ name }}
                </template>
              </v-select>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12 col-sm-6">
              <div class="mb-1">
                <small class="font-weight-bold">Risk Level</small>
              </div>
              <v-select
                v-model="filterForm.riskLevel"
                class="sm"
                multiple
                :clearable="true"
                :close-on-select="false"
                :options="riskLevelOptions"
                placeholder="Select risk level"
              />
            </div>
            <div class="col-12 col-sm-6">
              <div class="mb-1">
                <small class="font-weight-bold">Employee Type</small>
              </div>
              <v-select
                v-model="filterForm.employeeType"
                class="sm"
                :clearable="true"
                :reduce="getReduceValue"
                :options="employeeTypeOptions"
                placeholder="Select Employee"
              />
            </div>
            <!-- <div class="col-12 col-sm-6 mt-3 mt-sm-0">
              <div class="mb-1">
                <small class="font-weight-bold">Organization</small>
              </div>
              <v-select
                v-model="filterForm.organization"
                class="sm"
                :clearable="true"
                :close-on-select="true"
                :disabled="organizations.loading"
                label="name"
                :options="organizationOptions"
                :reduce="getReduceName"
                :selectable="option => !option.hasOwnProperty('group')"
                placeholder="Select Organization"
              >
                <template #option="{ group, name }">
                  <div v-if="group">
                    {{ group }}
                  </div>
                  {{ name }}
                </template>
              </v-select>
            </div> -->
          </div>
        </div>

        <div class="fdm-modal-footer">
          <button
            class="btn btn-outline-info btn-sm font-weight-bold"
            type="button"
            @click="resetFilter"
          >
            Reset
          </button>
          <button
            class="btn btn-info btn-sm font-weight-bold ml-2"
            type="button"
            @click="applyFilter"
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  </LayoutDashboard>
</template>

<script>
import { format, parseISO, sub } from 'date-fns'
import LayoutDashboard from '@/components/LayoutDashboard'
import VSelect from 'vue-select'
import DatePicker from 'vue2-datepicker'
import Api from '@/utils/Api'
import KpiCards from './components/KpiCards'
import FdmResult from './components/FdmResult'
import FdmPerformance from './components/FdmPerformance'
import ContractorDonut from './components/ContractorDonut'
import SummaryInsight from './components/SummaryInsight'
import DepartmentChart from './components/DepartmentChart'
import RiskBars from './components/RiskBars'
import TotalSubmitterGauge from './components/TotalSubmitterGauge'

export default {
  components: {
    LayoutDashboard,
    VSelect,
    DatePicker,
    KpiCards,
    FdmResult,
    FdmPerformance,
    ContractorDonut,
    SummaryInsight,
    DepartmentChart,
    RiskBars,
    TotalSubmitterGauge
  },

  data: () => ({
    performanceDefaultsApplied: false,
    stats: {
      totalEmployee: 0,
      fit: 0,
      unfit: 0,
      totalResponded: 0,
      fitFollowUp: 0,
      notFollowUp: 0,
      unsubmittedResponse: 0
    },
    fdmResult: {
      fit: { label: 'Fit', value: 0, total: 0, color: '#18B2BD' },
      followUp: { label: 'Fit Followup', value: 0, total: 0, color: '#F7D214' },
      unfit: { label: 'Unfit', value: 0, total: 0, color: '#FF0000' }
    },
    fdmPerformance: {
      categories: [],
      series: []
    },
    contractor: {
      label: 'All Contractor',
      value: 0,
      total: 0
    },
    contractorId: null,
    summaryInsight: [],
    department: {
      items: [],
      loading: false
    },
    workUnits: {
      all: [],
      loading: false,
      pageSize: 10,
      page: 1,
      search: ''
    },
    workUnitsDropdownEl: null,
    companiesDropdownEl: null,
    risk: {
      items: [],
      loading: false
    },
    unsubmittedModalOpen: false,
    unsubmittedSearch: '',
    unsubmittedSearchDebounce: null,
    unsubmittedPerPage: 10,
    unsubmittedPage: 1,
    unsubmittedFilterModalOpen: false,
    unsubmittedFilters: {
      companyName: null,
      department: null,
      position: null,
      status: null
    },
    unsubmittedFilterForm: {
      companyName: null,
      department: null,
      position: null,
      status: null
    },
    unsubmittedLoading: false,
    unsubmittedRows: [],
    unsubmittedMeta: {
      totalData: 0,
      totalPage: 1,
      pageSize: 10,
      currentPage: 1
    },
    organizations: {
      items: [],
      loading: false
    },
    companies: {
      items: [],
      loading: false,
      pageSize: 20,
      page: 1,
      hasMore: true
    },
    workLocationsCompany: [],
    filterModalOpen: false,
    filterForm: {
      period: 'Daily',
      dateRange: [new Date('2026-03-16'), new Date('2026-04-15')],
      department: '',
      employeeType: null,
      riskLevel: ['Low', 'Medium', 'High'],
      organization: null,
      workLocation: []
    }
  }),

  computed: {
    userRoleName () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        return profile?.role?.name || '-'
      } catch {
        return '-'
      }
    },
    isAdministratorSite () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        return String(profile?.role?.name || '').toLowerCase() === 'administrator site'
      } catch {
        return false
      }
    },
    isFunctionHeadSite () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        return String(profile?.role?.name || '').toLowerCase() === 'function head site'
      } catch {
        return false
      }
    },
    loggedInWorkUnit () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        const raw = profile?.workUnit || profile?.workUnitName || null
        const trimmed = raw ? String(raw).trim() : ''
        return trimmed || null
      } catch {
        return null
      }
    },
    periodOptions () {
      return ['Daily', 'Monthly']
    },
    departmentOptions () {
      return []
    },
    workUnitOptions () {
      if (this.isFunctionHeadSite) {
        return this.loggedInWorkUnit ? [this.loggedInWorkUnit] : []
      }

      const search = (this.workUnits.search || '').trim().toLowerCase()
      const all = (this.workUnits.all || [])
      const filtered = search
        ? all.filter(name => String(name).toLowerCase().includes(search))
        : all

      const limit = (Number(this.workUnits.page) || 1) * (Number(this.workUnits.pageSize) || 10)
      const sliced = filtered.slice(0, limit)
      return sliced
    },
    employeeTypeOptions () {
      return [{ label: 'Employee', value: true }, { label: 'Non Employee', value: false }]
    },
    riskLevelOptions () {
      return ['Low', 'Medium', 'High']
    },
    organizationOptions () {
      return this.organizations.items || []
    },
    unsubmittedCompanyOptions () {
      return this.companies.items || []
    },
    unsubmittedDepartmentOptions () {
      if (this.isFunctionHeadSite) {
        return this.loggedInWorkUnit ? [this.loggedInWorkUnit] : []
      }

      return (this.workUnits.all || [])
    },
    unsubmittedPositionOptions () {
      return [...new Set(this.unsubmittedRows.map(r => r.position).filter(Boolean))].sort()
    },
    unsubmittedStatusOptions () {
      return ['Contractor', 'ITM Group']
    },
    unsubmittedFilteredRows () {
      const search = (this.unsubmittedSearch || '').trim().toLowerCase()
      const filters = this.unsubmittedFilters
      return this.unsubmittedRows.filter(row => {
        if (filters.companyName && row.companyName !== filters.companyName) return false
        if (filters.department && row.department !== filters.department) return false
        if (filters.position && row.position !== filters.position) return false
        if (filters.status && row.status !== filters.status) return false

        if (!search) return true

        const haystack = [
          row.fullName,
          row.companyName,
          row.department,
          row.position,
          row.status
        ].join(' ').toLowerCase()

        return haystack.includes(search)
      })
    },
    unsubmittedTotal () {
      return Number(this.unsubmittedMeta.totalData) || 0
    },
    unsubmittedPageCount () {
      return Math.max(1, Number(this.unsubmittedMeta.totalPage) || 1)
    },
    unsubmittedPageRows () {
      return this.unsubmittedFilteredRows
    },
    unsubmittedFrom () {
      if (!this.unsubmittedTotal) return 0
      const currentPage = Number(this.unsubmittedMeta.currentPage) || 1
      const pageSize = Number(this.unsubmittedMeta.pageSize) || Number(this.unsubmittedPerPage) || 10
      return (currentPage - 1) * pageSize + 1
    },
    unsubmittedTo () {
      if (!this.unsubmittedTotal) return 0
      const currentPage = Number(this.unsubmittedMeta.currentPage) || 1
      const pageSize = Number(this.unsubmittedMeta.pageSize) || Number(this.unsubmittedPerPage) || 10
      return Math.min(this.unsubmittedTotal, (currentPage - 1) * pageSize + this.unsubmittedPageRows.length)
    },
    unsubmittedPages () {
      const total = this.unsubmittedPageCount
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1)

      const current = Number(this.unsubmittedMeta.currentPage) || this.unsubmittedPage
      const start = Math.max(1, Math.min(current - 2, total - 4))
      return Array.from({ length: 5 }, (_, i) => start + i)
    }
  },

  watch: {
    unsubmittedPerPage () {
      this.unsubmittedPage = 1
      this.fetchUnsubmittedDetail()
    },
    unsubmittedSearch () {
      this.unsubmittedPage = 1

      if (this.unsubmittedSearchDebounce) {
        clearTimeout(this.unsubmittedSearchDebounce)
      }

      this.unsubmittedSearchDebounce = setTimeout(() => {
        this.fetchUnsubmittedDetail()
      }, 350)
    },
    contractorId () {
      this.unsubmittedPage = 1
      this.fetchDepartment()
      this.fetchRiskLevel()
      this.fetchSummary()
      this.fetchAverageContractor()
      this.fetchUnsubmittedDetail()
      this.fetchFdmPerformance()
    }
  },

  async mounted () {
    await Promise.all([
      this.fetchWorkLocationsOrganization(),
      this.fetchWorkLocationsCompany(),
      this.fetchCompanies()
    ])
    this.applyDefaultPerformanceFilters()
    this.fetchDepartment()
    this.fetchRiskLevel()
    this.fetchSummary()
    this.fetchSummaryInsight()
    this.fetchAverageContractor()
    this.fetchUnsubmittedDetail()
    this.fetchWorkUnits()
    this.fetchFdmPerformance()
  },

  beforeDestroy () {
    if (this.workUnitsDropdownEl) {
      this.workUnitsDropdownEl.removeEventListener('scroll', this.onWorkUnitScroll)
      this.workUnitsDropdownEl = null
    }
    if (this.companiesDropdownEl) {
      this.companiesDropdownEl.removeEventListener('scroll', this.onCompanyScroll)
      this.companiesDropdownEl = null
    }
    if (this.unsubmittedSearchDebounce) {
      clearTimeout(this.unsubmittedSearchDebounce)
      this.unsubmittedSearchDebounce = null
    }
  },

  methods: {
    getLoggedInDefaultWorkLocationId () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        return (
          profile?.workLocationId ||
          profile?.workLocation?.id ||
          profile?.lastWorkLocationId ||
          profile?.lastWorkLocation?.id ||
          null
        )
      } catch {
        return null
      }
    },
    getLocationNameById (items, id) {
      if (!Array.isArray(items) || !id) return null
      const found = items.find(item => item && !item.group && item.id === id)
      return found?.name || null
    },
    getLoggedInCompanyId () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        return profile?.company?.id || profile?.companyId || null
      } catch {
        return null
      }
    },
    getLoggedInDefaultOrganization () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        return (
          profile?.company?.name ||
          profile?.companyName ||
          profile?.currentWorkLocation ||
          profile?.workLocation?.name ||
          profile?.workLocationActual ||
          profile?.organization?.name ||
          null
        )
      } catch {
        return null
      }
    },
    getLoggedInDefaultWorkLocation () {
      try {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}')
        return (
          profile?.currentWorkLocation ||
          profile?.workLocation?.name ||
          profile?.workLocationActual ||
          this.getLoggedInDefaultOrganization() ||
          null
        )
      } catch {
        return null
      }
    },
    applyDefaultPerformanceFilters () {
      if (this.performanceDefaultsApplied) return

      const defaultWorkLocationId = this.getLoggedInDefaultWorkLocationId()
      const organizationById = this.getLocationNameById(this.organizations.items, defaultWorkLocationId)
      const workLocationById = this.getLocationNameById(this.workLocationsCompany, defaultWorkLocationId)

      const organization = organizationById || this.getLoggedInDefaultOrganization()
      const workLocation = workLocationById || organizationById || this.getLoggedInDefaultWorkLocation()
      const existingWorkLocation = this.filterForm.workLocation
      const normalizedWorkLocation = Array.isArray(existingWorkLocation)
        ? existingWorkLocation
        : (existingWorkLocation ? [existingWorkLocation] : [])

      this.filterForm = {
        ...this.filterForm,
        period: 'Daily',
        dateRange: [new Date('2026-03-16'), new Date('2026-04-15')],
        organization: organization || this.filterForm.organization,
        workLocation: workLocation ? [workLocation] : normalizedWorkLocation,
        riskLevel: Array.isArray(this.filterForm.riskLevel) && this.filterForm.riskLevel.length
          ? this.filterForm.riskLevel
          : ['Low', 'Medium', 'High']
      }

      if (this.isFunctionHeadSite && this.loggedInWorkUnit) {
        this.filterForm = {
          ...this.filterForm,
          department: this.loggedInWorkUnit
        }
      }

      this.performanceDefaultsApplied = true
    },
    getReduceId: ({ id }) => id,
    getReduceName: ({ name }) => name,
    getReduceValue: ({ value }) => value,
    buildEmployeeTypeQueryParts (paramName = 'employeeType') {
      const employeeType = this.filterForm.employeeType
      if (typeof employeeType !== 'boolean') return []

      const ordered = employeeType ? [true, false] : [false, true]
      return ordered.map(value => `${paramName}=${value}`)
    },
    buildWorkLocationQueryParts (paramName = 'workLocation', overrideValues) {
      const workLocation = overrideValues !== undefined ? overrideValues : this.filterForm.workLocation
      const values = Array.isArray(workLocation)
        ? workLocation
        : (workLocation ? [workLocation] : [])

      return values
        .map(v => String(v).trim())
        .filter(Boolean)
        .map(v => `${paramName}=${encodeURIComponent(v)}`)
    },
    async fetchWorkLocationsOrganization () {
      try {
        this.organizations.loading = true
        const { data } = await Api.masterData.customWorkLocations({
          Filters: 'IsOrganization==true'
        })

        const raw = Array.isArray(data?.data)
          ? data.data
          : (Array.isArray(data) ? data : [])

        const items = raw.reduce((acc, curr) => {
          const groupLabel = curr.category === 'site' ? 'Sites' : 'Others'
          const newGroup = (!acc.find(a => a.group === groupLabel) && [{ group: groupLabel, name: '' }]) || []
          return [
            ...acc,
            ...newGroup,
            curr
          ]
        }, [])

        this.organizations.items = items
      } catch {
        this.organizations.items = []
      } finally {
        this.organizations.loading = false
      }
    },
    async fetchWorkLocationsCompany () {
      const companyId = this.getLoggedInCompanyId()
      if (!companyId) {
        this.workLocationsCompany = []
        return
      }

      try {
        const { data } = await Api.masterData.workLocations({
          Filters: `companyId==${companyId}`
        })

        const raw = Array.isArray(data?.data)
          ? data.data
          : (Array.isArray(data) ? data : [])

        const items = raw.reduce((acc, curr) => {
          const groupLabel = curr.category === 'site' ? 'Sites' : 'Others'
          const newGroup = (!acc.find(a => a.group === groupLabel) && [{ group: groupLabel, name: '' }]) || []
          return [
            ...acc,
            ...newGroup,
            curr
          ]
        }, [])

        this.workLocationsCompany = items
      } catch {
        this.workLocationsCompany = []
      }
    },
    async fetchCompanies () {
      try {
        this.companies.loading = true
        const page = Number(this.companies.page) || 1
        const pageSize = Number(this.companies.pageSize) || 20
        const { data } = await Api.companies.custom({ Page: page, PageSize: pageSize })

        const items = Array.isArray(data?.data)
          ? data.data
          : (Array.isArray(data?.data?.data) ? data.data.data : [])

        if (!Array.isArray(items) || !items.length) {
          this.companies.hasMore = false
          return
        }
        if (items.length < pageSize) {
          this.companies.hasMore = false
        }

        const next = page === 1
          ? items
          : [
            ...(this.companies.items || []),
            ...items
          ]

        const uniqueById = next.reduce((acc, curr) => {
          if (!curr?.id) return acc
          if (acc.seen.has(curr.id)) return acc
          acc.seen.add(curr.id)
          acc.items.push(curr)
          return acc
        }, { items: [], seen: new Set() }).items

        this.companies.items = uniqueById
      } catch {
        this.companies.items = []
        this.companies.hasMore = false
      } finally {
        this.companies.loading = false
      }
    },
    onCompanyOpen () {
      if (!Array.isArray(this.companies.items) || !this.companies.items.length) {
        this.companies.page = 1
        this.companies.hasMore = true
        this.fetchCompanies()
      }

      this.$nextTick(() => {
        const dropdownEl = this.$refs.unsubmittedCompanySelect?.$el?.querySelector('.vs__dropdown-menu')
        if (!dropdownEl) return

        if (this.companiesDropdownEl && this.companiesDropdownEl !== dropdownEl) {
          this.companiesDropdownEl.removeEventListener('scroll', this.onCompanyScroll)
        }

        this.companiesDropdownEl = dropdownEl
        this.companiesDropdownEl.removeEventListener('scroll', this.onCompanyScroll)
        this.companiesDropdownEl.addEventListener('scroll', this.onCompanyScroll)
      })
    },
    onCompanyScroll (e) {
      const el = e?.target
      if (!el) return
      if (this.companies.loading) return
      if (!this.companies.hasMore) return

      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
      if (!nearBottom) return

      this.companies.page = (Number(this.companies.page) || 1) + 1
      this.fetchCompanies()
    },
    buildDepartmentQuery () {
      const period = (this.filterForm.period || 'Daily').toLowerCase()
      const startDate = this.filterForm.dateRange?.[0] || sub(new Date(), { days: 30 })
      const endDate = this.filterForm.dateRange?.[1] || new Date()
      const workUnit = this.filterForm.department
      const contractorId = this.contractorId

      const base = [
        `period=${period}`,
        `startDate=${format(startDate, 'yyyy-MM-dd')}`,
        `endDate=${format(endDate, 'yyyy-MM-dd')}`
      ]

      base.push(...this.buildWorkLocationQueryParts('workLocation'))
      if (workUnit) {
        base.push(`workUnit=${encodeURIComponent(workUnit)}`)
      }
      if (contractorId) {
        base.push(`contractorId=${encodeURIComponent(contractorId)}`)
      }

      base.push(...this.buildEmployeeTypeQueryParts('employeeType'))

      return base.join('&')
    },
    async fetchDepartment () {
      try {
        this.department.loading = true
        const { data } = await Api.dashboard.submissionDepartment(this.buildDepartmentQuery())

        const colors = ['#FF7A00', '#006CA0', '#FF0000', '#00F60A', '#18B2BD', '#F7D214', '#4C2E88', '#9CA3AF']
        const keys = Object.keys(data || {})
        const values = Object.values(data || {})

        this.department.items = keys.map((name, i) => ({
          name,
          y: values[i],
          color: colors[i % colors.length]
        }))
      } catch {
        this.department.items = this.department.items.length ? this.department.items : []
      } finally {
        this.department.loading = false
      }
    },
    async fetchWorkUnits () {
      try {
        this.workUnits.loading = true
        const { data } = await Api.workUnits.list()

        const raw = Array.isArray(data?.data)
          ? data.data
          : (Array.isArray(data) ? data : [])

        const names = raw
          .map(item => item?.workUnitName)
          .filter(Boolean)
          .map(name => String(name).trim())
          .filter(Boolean)
          .filter(name => name.toLowerCase() !== 'all')

        const uniqueSorted = [...new Set(names)].sort((a, b) => a.localeCompare(b))

        this.workUnits.all = uniqueSorted
        this.workUnits.page = 1
      } catch {
        this.workUnits.all = []
        this.workUnits.page = 1
      } finally {
        this.workUnits.loading = false
      }
    },
    onWorkUnitSearch (search) {
      this.workUnits.search = search || ''
      this.workUnits.page = 1
    },
    onWorkUnitOpen () {
      this.$nextTick(() => {
        const dropdownEl = this.$refs.workUnitSelect?.$el?.querySelector('.vs__dropdown-menu')
        if (!dropdownEl) return

        if (this.workUnitsDropdownEl && this.workUnitsDropdownEl !== dropdownEl) {
          this.workUnitsDropdownEl.removeEventListener('scroll', this.onWorkUnitScroll)
        }

        this.workUnitsDropdownEl = dropdownEl
        this.workUnitsDropdownEl.removeEventListener('scroll', this.onWorkUnitScroll)
        this.workUnitsDropdownEl.addEventListener('scroll', this.onWorkUnitScroll)
      })
    },
    onWorkUnitScroll (e) {
      const el = e?.target
      if (!el) return

      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
      if (!nearBottom) return

      const search = (this.workUnits.search || '').trim().toLowerCase()
      const all = (this.workUnits.all || [])
      const filteredCount = search
        ? all.filter(name => String(name).toLowerCase().includes(search)).length
        : all.length

      const pageSize = Number(this.workUnits.pageSize) || 10
      const currentLimit = (Number(this.workUnits.page) || 1) * pageSize
      if (currentLimit >= filteredCount) return

      this.workUnits.page = (Number(this.workUnits.page) || 1) + 1
    },
    buildRiskLevelQuery () {
      const period = (this.filterForm.period || 'Daily').toLowerCase()
      const startDate = this.filterForm.dateRange?.[0] || sub(new Date(), { days: 30 })
      const endDate = this.filterForm.dateRange?.[1] || new Date()
      const workUnit = this.filterForm.department
      const contractorId = this.contractorId

      const base = [
        `period=${period}`,
        `startDate=${format(startDate, 'yyyy-MM-dd')}`,
        `endDate=${format(endDate, 'yyyy-MM-dd')}`
      ]

      base.push(...this.buildWorkLocationQueryParts('workLocation'))
      if (workUnit) {
        base.push(`workUnit=${encodeURIComponent(workUnit)}`)
      }
      if (contractorId) {
        base.push(`contractorId=${encodeURIComponent(contractorId)}`)
      }

      base.push(...this.buildEmployeeTypeQueryParts('employeeType'))

      return base.join('&')
    },
    buildAverageContractorQuery () {
      const period = (this.filterForm.period || 'Daily').toLowerCase()
      const startDate = this.filterForm.dateRange?.[0] || sub(new Date(), { days: 30 })
      const endDate = this.filterForm.dateRange?.[1] || new Date()
      const workUnit = this.filterForm.department
      const contractorId = this.contractorId

      const base = [
        `period=${period}`,
        `startDate=${format(startDate, 'yyyy-MM-dd')}`,
        `endDate=${format(endDate, 'yyyy-MM-dd')}`
      ]

      if (workUnit) {
        base.push(`workUnit=${encodeURIComponent(workUnit)}`)
      }
      if (contractorId) {
        base.push(`contractorId=${encodeURIComponent(contractorId)}`)
      }

      base.push(...this.buildEmployeeTypeQueryParts('employeeType'))

      return base.join('&')
    },
    buildLineChartQuery () {
      const period = (this.filterForm.period || 'Daily').toLowerCase()
      const startDate = this.filterForm.dateRange?.[0] || new Date('2026-03-16')
      const endDate = this.filterForm.dateRange?.[1] || new Date('2026-04-15')
      const workUnit = this.filterForm.department
      const contractorId = this.contractorId

      const base = [
        `period=${period}`,
        `startDate=${format(startDate, 'yyyy-MM-dd')}`,
        `endDate=${format(endDate, 'yyyy-MM-dd')}`
      ]

      base.push(...this.buildWorkLocationQueryParts('workLocation'))
      if (workUnit) {
        base.push(`workUnit=${encodeURIComponent(workUnit)}`)
      }
      if (contractorId) {
        base.push(`contractorId=${encodeURIComponent(contractorId)}`)
      }

      base.push(...this.buildEmployeeTypeQueryParts('employeeType'))

      const risks = Array.isArray(this.filterForm.riskLevel)
        ? this.filterForm.riskLevel
        : (this.filterForm.riskLevel ? [this.filterForm.riskLevel] : ['Low', 'Medium', 'High'])

      risks
        .map(r => String(r).toLowerCase())
        .filter(Boolean)
        .forEach(risk => base.push(`risk=${encodeURIComponent(risk)}`))

      return base.join('&')
    },
    async fetchFdmPerformance () {
      try {
        const { data } = await Api.dashboard.lineChart(this.buildLineChartQuery())

        const period = (this.filterForm.period || 'Daily').toLowerCase()
        const dateFormat = period === 'monthly' ? 'MMM' : 'dd MMM'

        const colors = {
          low: '#00F60A',
          medium: '#F7D214',
          high: '#FF0000'
        }

        const risks = Array.isArray(this.filterForm.riskLevel)
          ? this.filterForm.riskLevel
          : (this.filterForm.riskLevel ? [this.filterForm.riskLevel] : ['low', 'medium', 'high'])

        const normalizedRisks = risks
          .map(r => String(r).toLowerCase())
          .filter(Boolean)

        const dates = Object.keys(data || {}).sort()
        const valuesByDate = dates.map(d => data?.[d] || {})

        this.fdmPerformance.categories = dates.map(d => {
          try {
            return format(parseISO(d), dateFormat)
          } catch {
            return d
          }
        })

        const nameMap = { low: 'Fit', medium: 'Fit Follow-up', high: 'Unfit' }
        this.fdmPerformance.series = normalizedRisks.map(risk => ({
          name: nameMap[risk] || `${risk.charAt(0).toUpperCase()}${risk.slice(1)}`,
          color: colors[risk] || '#18B2BD',
          data: valuesByDate.map(v => Number(v?.[risk]) || 0)
        }))
      } catch {
        this.fdmPerformance.categories = []
        this.fdmPerformance.series = []
      }
    },
    async fetchRiskLevel () {
      try {
        this.risk.loading = true
        const { data } = await Api.dashboard.riskLevel(this.buildRiskLevelQuery())

        this.risk.items = [
          { label: 'Low', value: Number(data?.low) || 0, color: '#00F60A' },
          { label: 'Medium', value: Number(data?.medium) || 0, color: '#FF7A00' },
          { label: 'High', value: Number(data?.high) || 0, color: '#FF0000' }
        ]
      } catch {
        this.risk.items = this.risk.items.length ? this.risk.items : []
      } finally {
        this.risk.loading = false
      }
    },
    async fetchSummary () {
      try {
        const { data } = await Api.dashboard.summary(this.buildRiskLevelQuery())
        const fitFollowUp = Number(data?.fitFollowUp ?? data?.notFollowUp) || 0

        this.stats = {
          totalEmployee: Number(data?.totalEmployee) || 0,
          fit: Number(data?.fit) || 0,
          unfit: Number(data?.unfit) || 0,
          totalResponded: Number(data?.totalResponded) || 0,
          fitFollowUp,
          notFollowUp: fitFollowUp,
          unsubmittedResponse: Number(data?.unsubmittedResponse) || 0
        }

        const totalResponded = Number(this.stats.totalResponded) || 0
        const fit = Number(this.stats.fit) || 0

        this.fdmResult = {
          fit: { ...this.fdmResult.fit, value: fit, total: totalResponded },
          followUp: { ...this.fdmResult.followUp, value: Number(this.stats.fitFollowUp) || 0, total: totalResponded },
          unfit: { ...this.fdmResult.unfit, value: Number(this.stats.unfit) || 0, total: totalResponded }
        }
      } catch {
        // Keep previous values if request fails
      }
    },
    async fetchSummaryInsight () {
      try {
        const { data } = await Api.dashboard.fitUnfitRateComparison(this.buildRiskLevelQuery())

        const fitRate = Number(data?.current?.fitRate) || 0
        const unfitRate = Number(data?.current?.unfitRate) || 0
        const fitChange = Number(data?.change?.fitRate) || 0
        const unfitChange = Number(data?.change?.unfitRate) || 0
        const fitTrend = String(data?.trend?.fitRate || '').toLowerCase() === 'down' ? 'down' : 'up'
        const unfitTrend = String(data?.trend?.unfitRate || '').toLowerCase() === 'down' ? 'down' : 'up'

        const formatPercent = value => `${Number(value).toFixed(2).replace(/\.00$/, '')}%`

        this.summaryInsight = [
          {
            label: 'Fit Rate',
            value: formatPercent(fitRate),
            delta: formatPercent(Math.abs(fitChange)),
            direction: fitTrend,
            icon: 'fa-heart'
          },
          {
            label: 'Unfit Rate',
            value: formatPercent(unfitRate),
            delta: formatPercent(Math.abs(unfitChange)),
            direction: unfitTrend,
            icon: 'fa-medkit'
          }
        ]
      } catch {
        this.summaryInsight = this.summaryInsight.length ? this.summaryInsight : []
      }
    },
    async fetchAverageContractor () {
      try {
        const { data } = await Api.dashboard.averageContractor(this.buildAverageContractorQuery())
        const averageSubmitterRaw = Number(data?.averageSubmitter) || 0
        const averageSubmitter = Number.isInteger(averageSubmitterRaw)
          ? averageSubmitterRaw
          : Number(averageSubmitterRaw.toFixed(2))

        this.contractor = {
          ...this.contractor,
          value: averageSubmitter,
          total: Number(data?.totalContractor) || 0
        }
      } catch {
        this.contractor = { ...this.contractor, value: 0, total: 0 }
      }
    },
    buildUnsubmittedDetailQuery (page, pageSize) {
      const period = (this.filterForm.period || 'Daily').toLowerCase()
      const startDate = this.filterForm.dateRange?.[0] || sub(new Date(), { days: 30 })
      const endDate = this.filterForm.dateRange?.[1] || new Date()
      const workUnit = this.unsubmittedFilters?.department || this.filterForm.department
      const contractorId = this.contractorId
      const companyName = this.unsubmittedFilters?.companyName || null
      const companyGroup = this.unsubmittedFilters?.status || null
      const search = (this.unsubmittedSearch || '').trim()
      // const organization = this.filterForm.organization

      const base = [
        `Period=${encodeURIComponent(period)}`,
        `StartDate=${format(startDate, 'yyyy-MM-dd')}`,
        `EndDate=${format(endDate, 'yyyy-MM-dd')}`
      ]

      // if (organization) {
      //   base.push(`Organization=${encodeURIComponent(organization)}`)
      // }
      if (companyName) {
        base.push(`CompanyName=${encodeURIComponent(companyName)}`)
      }
      if (companyGroup) {
        base.push(`CompanyGroup=${encodeURIComponent(companyGroup)}`)
      }
      if (search) {
        base.push(`search=${encodeURIComponent(search)}`)
      }
      base.push(...this.buildWorkLocationQueryParts('WorkLocation'))
      if (workUnit) {
        base.push(`WorkUnit=${encodeURIComponent(workUnit)}`)
      }
      if (contractorId) {
        base.push(`ContractorId=${encodeURIComponent(contractorId)}`)
      }
      base.push(...this.buildEmployeeTypeQueryParts('EmployeeType'))
      if (page) {
        base.push(`Page=${encodeURIComponent(page)}`)
      }
      if (pageSize) {
        base.push(`PageSize=${encodeURIComponent(pageSize)}`)
      }

      return base.join('&')
    },
    async fetchUnsubmittedDetail () {
      if (this.unsubmittedLoading) return

      try {
        this.unsubmittedLoading = true
        this.unsubmittedRows = []
        const page = Number(this.unsubmittedPage) || 1
        const pageSize = Number(this.unsubmittedPerPage) || 10
        const query = this.buildUnsubmittedDetailQuery(page, pageSize)
        const { data } = await Api.dashboard.summaryUnsubmittedDetail(query)
        this.unsubmittedMeta = {
          totalData: Number(data?.totalData) || 0,
          totalPage: Number(data?.totalPage) || 1,
          pageSize: Number(data?.pageSize) || pageSize,
          currentPage: Number(data?.currentPage) || page
        }
        const items = Array.isArray(data?.data) ? data.data : []
        const mapped = items.map((item) => {
          return {
            id: item?.id,
            fullName: item?.fullname || '',
            companyName: item?.company?.name || '',
            department: item?.workUnit || '',
            position: item?.position || '',
            status: item?.companyGroup || ''
          }
        })
        this.unsubmittedRows = mapped
      } catch {
        this.unsubmittedRows = []
        this.unsubmittedMeta = {
          totalData: 0,
          totalPage: 1,
          pageSize: Number(this.unsubmittedPerPage) || 10,
          currentPage: Number(this.unsubmittedPage) || 1
        }
      } finally {
        this.unsubmittedLoading = false
      }
    },
    openFilterModal () {
      this.filterModalOpen = true
    },
    closeFilterModal () {
      this.filterModalOpen = false
    },
    resetFilter () {
      this.filterForm = {
        period: 'Daily',
        dateRange: [new Date('2026-03-16'), new Date('2026-04-15')],
        department: '',
        employeeType: null,
        riskLevel: ['Low', 'Medium', 'High'],
        organization: null,
        workLocation: []
      }
      this.performanceDefaultsApplied = false
      this.applyDefaultPerformanceFilters()
      this.fetchDepartment()
      this.fetchRiskLevel()
      this.fetchSummary()
      this.fetchSummaryInsight()
      this.fetchAverageContractor()
      this.unsubmittedPage = 1
      this.fetchUnsubmittedDetail()
      this.fetchFdmPerformance()
    },
    applyFilter () {
      this.filterModalOpen = false
      this.fetchDepartment()
      this.fetchRiskLevel()
      this.fetchSummary()
      this.fetchSummaryInsight()
      this.fetchAverageContractor()
      this.unsubmittedPage = 1
      this.fetchUnsubmittedDetail()
      this.fetchFdmPerformance()
    },
    openUnsubmittedResponseModal () {
      this.unsubmittedPage = 1
      this.unsubmittedSearch = ''
      this.fetchUnsubmittedDetail()
      this.unsubmittedModalOpen = true
    },
    closeUnsubmittedResponseModal () {
      this.unsubmittedModalOpen = false
    },
    openUnsubmittedFilterModal () {
      this.unsubmittedFilterForm = {
        companyName: this.unsubmittedFilters.companyName,
        department: this.unsubmittedFilters.department,
        position: this.unsubmittedFilters.position,
        status: this.unsubmittedFilters.status
      }
      this.unsubmittedFilterModalOpen = true
    },
    closeUnsubmittedFilterModal () {
      this.unsubmittedFilterModalOpen = false
    },
    resetUnsubmittedFilter () {
      this.unsubmittedFilterForm = {
        companyName: null,
        department: null,
        position: null,
        status: null
      }
    },
    applyUnsubmittedFilter () {
      this.unsubmittedFilters = {
        companyName: this.unsubmittedFilterForm.companyName || null,
        department: this.unsubmittedFilterForm.department || null,
        position: this.unsubmittedFilterForm.position || null,
        status: this.unsubmittedFilterForm.status || null
      }
      this.unsubmittedPage = 1
      this.unsubmittedSearch = ''
      this.unsubmittedFilterModalOpen = false
      this.fetchUnsubmittedDetail()
    },
    setUnsubmittedPage (page) {
      const safe = Math.min(this.unsubmittedPageCount, Math.max(1, page))
      this.unsubmittedPage = safe
      this.fetchUnsubmittedDetail()
    }
  }
}
</script>

<style scoped>
.fdm-modal-backdrop {
  align-items: center;
  background: rgba(0, 0, 0, 0.55);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 18px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 2000;
}

.fdm-modal-backdrop-top {
  z-index: 2100;
}

.fdm-modal {
  background: #ffffff;
  border-radius: 12px;
  max-width: 560px;
  width: 100%;
}

.fdm-modal-wide {
  max-width: 980px;
}

.fdm-modal-header {
  align-items: center;
  border-bottom: 1px solid #ededed;
  display: flex;
  justify-content: space-between;
  padding: 16px 18px;
}

.fdm-modal-close {
  background: transparent;
  border: 0;
  color: #18b2bd;
  font-size: 18px;
  line-height: 1;
  padding: 0;
}

.fdm-modal-body {
  padding: 16px 18px;
}

.fdm-unsubmitted-card {
  border: 1px solid #ededed;
  border-radius: 12px;
  padding: 14px 14px 10px;
}

.fdm-unsubmitted-footer {
  align-items: center;
  border-top: 1px solid #ededed;
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 12px;
}

.fdm-modal-footer {
  border-top: 1px solid #ededed;
  display: flex;
  justify-content: flex-end;
  padding: 14px 18px 18px;
}

.fdm-modal-footer-split {
  align-items: center;
  justify-content: space-between;
}

.fdm-table {
  border-collapse: separate;
  border-spacing: 0 10px;
  margin-bottom: 0;
}

.fdm-table thead th {
  border-bottom: 1px solid #ededed;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 10px 12px;
  white-space: nowrap;
}

.fdm-table tbody tr {
  background: #f5f7ff;
}

.fdm-table tbody td {
  font-size: 13px;
  padding: 12px 10px;
  vertical-align: middle;
}

.fdm-table tbody tr td:first-child {
  border-bottom-left-radius: 10px;
  border-top-left-radius: 10px;
}

.fdm-table tbody tr td:last-child {
  border-bottom-right-radius: 10px;
  border-top-right-radius: 10px;
}

.fdm-pagination .page-link {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: #9ca3af;
  display: flex;
  height: 32px;
  justify-content: center;
  min-width: 32px;
  padding: 0;
}

.fdm-page-number.active .page-link {
  background: #18b2bd;
  color: #ffffff;
}

.fdm-page-nav .page-link {
  color: #ffffff;
}

.fdm-page-prev .page-link {
  background: #d1d5db;
}

.fdm-page-next .page-link {
  background: #18b2bd;
}

.fdm-page-nav.disabled .page-link {
  background: #e5e7eb;
  color: #ffffff;
  opacity: 0.9;
}

.status-pill {
  border-radius: 999px;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
}

.status-contractor {
  background: rgba(24, 178, 189, 0.15);
  color: #18b2bd;
}

.status-employee {
  background: rgba(245, 158, 11, 0.2);
  color: #b45309;
}
</style>
